import { useState, useEffect } from 'react'
import { useAction, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { TrendingUp, TrendingDown, Users, BarChart3, Loader2 } from 'lucide-react'

interface Metrics {
  arr: number
  arrDelta7d: number
  customers: number
  customersDelta7d: number
  newCustomers24h: number
}

const PERIOD_OPTIONS = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
] as const

const FUNNEL_STAGES = [
  { key: 'visitors', label: 'Visitors', color: '#8B5CF6' },
  { key: 'signups', label: 'Signups', color: '#818CF8' },
  { key: 'projects', label: 'Videos', color: '#3B82F6' },
  { key: 'paid', label: 'Paid', color: '#06B6D4' },
] as const

export default function AdminDashboardPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const getMetrics = useAction(api.chartmogul.getMetrics)
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [periodHours, setPeriodHours] = useState(24)
  const [visitorPeriod, setVisitorPeriod] = useState(24)
  const [referralPeriod, setReferralPeriod] = useState(24)
  const [showAllVisitors, setShowAllVisitors] = useState(false)
  const [showAllReferrals, setShowAllReferrals] = useState(false)

  const funnel = useQuery(api.analytics.getFunnelMetrics, isAdmin ? { periodHours } : 'skip')
  const visitorFunnel = useQuery(api.analytics.getFunnelMetrics, isAdmin ? { periodHours: visitorPeriod } : 'skip')
  const referrals = useQuery(api.adminQueries.getReferralsBreakdown, isAdmin ? { periodHours: referralPeriod, onlyPaid: true } : 'skip') as { total: number; entries: { label: string; count: number; pct: number }[] } | undefined
  const signupsPerDay = useQuery(api.adminQueries.getSignupsPerDay, isAdmin ? {} : 'skip')

  useEffect(() => {
    if (!isAdmin) return
    setLoading(true)
    getMetrics()
      .then(setMetrics)
      .catch((err) => console.error('[AdminDashboard] Failed to fetch metrics:', err))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`
    }
    return `$${Math.round(value).toLocaleString()}`
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
        <p className="text-sm text-neutral-500">Footage platform metrics overview</p>
      </div>

      {loading || !metrics ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total ARR */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 text-neutral-400" />
              <span className="text-sm text-neutral-500">Total ARR</span>
            </div>
            <p className="text-2xl font-semibold text-neutral-900">{formatCurrency(metrics.arr)}</p>
          </div>

          {/* Net ARR Last 7d */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              {metrics.arrDelta7d >= 0 ? (
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              <span className="text-sm text-neutral-500">Net ARR Last 7d</span>
            </div>
            <p
              className={cn(
                'text-2xl font-semibold',
                metrics.arrDelta7d >= 0 ? 'text-emerald-600' : 'text-red-600'
              )}
            >
              {metrics.arrDelta7d >= 0 ? '+' : ''}{metrics.arrDelta7d}%
            </p>
          </div>

          {/* Total Paying Customers */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-neutral-400" />
              <span className="text-sm text-neutral-500">Total Paying Customers</span>
            </div>
            <p className="text-2xl font-semibold text-neutral-900">{metrics.customers}</p>
          </div>

          {/* Net Customers Last 7d */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-neutral-400" />
              <span className="text-sm text-neutral-500">Net Customers Last 7d</span>
            </div>
            <p
              className={cn(
                'text-2xl font-semibold',
                metrics.customersDelta7d >= 0 ? 'text-emerald-600' : 'text-red-600'
              )}
            >
              {metrics.customersDelta7d >= 0 ? '+' : ''}{metrics.customersDelta7d}
            </p>
          </div>
        </div>
      )}

      {/* Conversion Funnel */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 pb-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xs font-semibold text-neutral-400 tracking-widest uppercase">
            Conversion Funnel
          </h2>
          <div className="flex gap-1 bg-neutral-100 rounded-lg p-0.5">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.hours}
                onClick={() => setPeriodHours(opt.hours)}
                className={cn(
                  'px-3 py-1 text-sm font-medium rounded-md transition-colors',
                  periodHours === opt.hours
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {funnel === undefined ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : (() => {
          const totals = funnel.totals
          const maxCount = Math.max(totals.visitors, 1)

          const gradColors: [string, string][] = [
            ['#a78bfa', '#9b8afb'],
            ['#818cf8', '#6d8ff8'],
            ['#60a5fa', '#5bb8f9'],
            ['#67e8f9', '#a5f3fc'],
          ]

          return (
            <div className="relative">
              {/* Funnel columns */}
              <div className="grid grid-cols-4 gap-3">
                {FUNNEL_STAGES.map((stage, i) => {
                  const count = totals[stage.key as keyof typeof totals]
                  const ratio = count / maxCount
                  const prevRatio = i > 0
                    ? totals[FUNNEL_STAGES[i - 1].key as keyof typeof totals] / maxCount
                    : ratio

                  // Columns 1 & 2 get a slanted top (left edge = prev height, right edge = current height)
                  // Columns 0 & 3 are flat
                  const svgH = 240
                  const svgW = 150
                  const topLeft = svgH - (i === 1 || i === 2 ? prevRatio : ratio) * (svgH - 4)
                  const topRight = svgH - ratio * (svgH - 4)
                  const path = `M 0,${topLeft} L ${svgW},${topRight} L ${svgW},${svgH} L 0,${svgH} Z`

                  const prevCount = i > 0
                    ? totals[FUNNEL_STAGES[i - 1].key as keyof typeof totals]
                    : null
                  const stepRate = prevCount !== null && prevCount > 0
                    ? `${Math.round((count / prevCount) * 1000) / 10}%`
                    : null

                  return (
                    <div key={stage.key} className="rounded-xl overflow-hidden">
                      <svg
                        viewBox={`0 0 ${svgW} ${svgH}`}
                        className="w-full"
                        style={{ height: 240 }}
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <linearGradient id={`fg-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={gradColors[i][0]} />
                            <stop offset="100%" stopColor={gradColors[i][1]} />
                          </linearGradient>
                        </defs>
                        <path d={path} fill={`url(#fg-${i})`} />
                      </svg>
                      <div className="pt-3 text-center">
                        <div className="flex items-baseline justify-center gap-1.5">
                          <span className="text-3xl font-bold" style={{ color: stage.color }}>
                            {count}
                          </span>
                          <span className="text-sm font-semibold text-neutral-800">{stage.label}</span>
                        </div>
                        <p className="text-xs text-neutral-400 mt-0.5">
                          {stepRate !== null ? (
                            <>Conv: <span className="font-medium text-neutral-500">{stepRate}</span></>
                          ) : '\u00A0'}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Signups Per Day */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6">
        <h2 className="text-xs font-semibold text-neutral-400 tracking-widest uppercase mb-5">
          Signups Per Day (Last 30 Days)
        </h2>
        {signupsPerDay === undefined ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : (() => {
          const maxCount = Math.max(...signupsPerDay.map(d => d.count), 1)
          const total = signupsPerDay.reduce((s, d) => s + d.count, 0)
          return (
            <div>
              <div className="flex items-end gap-[3px]" style={{ height: 160 }}>
                {signupsPerDay.map((day) => {
                  const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0
                  return (
                    <div
                      key={day.date}
                      className="flex-1 group relative"
                      style={{ height: '100%' }}
                    >
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-indigo-500 hover:bg-indigo-600 transition-colors"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-neutral-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {day.count}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-between mt-3 text-xs text-neutral-400">
                <span>{new Date(signupsPerDay[0].date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <span className="text-neutral-600 font-medium">{total} total</span>
                <span>{new Date(signupsPerDay[signupsPerDay.length - 1].date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
            </div>
          )
        })()}
      </div>

      {/* Bottom row: Conversion + Referrals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Visitors Referrals */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-neutral-900">
              New Visitors Referrals
            </h2>
            <div className="flex gap-1 bg-neutral-100 rounded-lg p-0.5">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.hours}
                  onClick={() => { setVisitorPeriod(opt.hours); setShowAllVisitors(false) }}
                  className={cn(
                    'px-2.5 py-0.5 text-xs font-medium rounded-md transition-colors',
                    visitorPeriod === opt.hours
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {visitorFunnel === undefined ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : visitorFunnel.channels.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">No visitor data</p>
          ) : (() => {
            const totalVisitors = visitorFunnel.channels.reduce((sum, ch) => sum + ch.visitors, 0)
            const visibleChannels = showAllVisitors ? visitorFunnel.channels : visitorFunnel.channels.slice(0, 10)
            return (
              <div className="space-y-4">
                {visibleChannels.map((entry) => {
                  const maxCount = visitorFunnel.channels[0].visitors
                  const barWidth = maxCount > 0 ? (entry.visitors / maxCount) * 100 : 0
                  const pct = totalVisitors > 0 ? Math.round((entry.visitors / totalVisitors) * 1000) / 10 : 0
                  return (
                    <div key={entry.channel}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-neutral-700">{entry.channel}</span>
                        <span className="text-sm text-neutral-500">
                          {entry.visitors} ({pct}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-violet-500 rounded-full transition-all"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
                {visitorFunnel.channels.length > 10 && (
                  <button
                    onClick={() => setShowAllVisitors(!showAllVisitors)}
                    className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                  >
                    {showAllVisitors ? 'Show less' : `Show all ${visitorFunnel.channels.length} sources`}
                  </button>
                )}
              </div>
            )
          })()}
          <p className="text-xs text-neutral-400 mt-6">Source: Convex</p>
        </div>

        {/* New Paid Accounts Referrals */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-neutral-900">
              New Paid Accounts Referrals
            </h2>
            <div className="flex gap-1 bg-neutral-100 rounded-lg p-0.5">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.hours}
                  onClick={() => { setReferralPeriod(opt.hours); setShowAllReferrals(false) }}
                  className={cn(
                    'px-3 py-1 text-sm font-medium rounded-md transition-colors',
                    referralPeriod === opt.hours
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {referrals === undefined ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : referrals.entries.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">No referral data</p>
          ) : (
            <div className="space-y-4">
              {(showAllReferrals ? referrals.entries : referrals.entries.slice(0, 10)).map((entry) => {
                const maxCount = referrals.entries[0].count
                const barWidth = maxCount > 0 ? (entry.count / maxCount) * 100 : 0
                return (
                  <div key={entry.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-neutral-700">{entry.label}</span>
                      <span className="text-sm text-neutral-500">
                        {entry.count} ({entry.pct}%)
                      </span>
                    </div>
                    <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                )
              })}
              {referrals.entries.length > 10 && (
                <button
                  onClick={() => setShowAllReferrals(!showAllReferrals)}
                  className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  {showAllReferrals ? 'Show less' : `Show all ${referrals.entries.length} sources`}
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-neutral-400 mt-6">Source: Convex</p>
        </div>
      </div>
    </div>
  )
}
